import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ImagePlus, Film, ArrowUp, ArrowDown, GripVertical, FileText, Sparkles, Wand2, Zap, CalendarClock, X, FileEdit, Archive, ChevronUp, ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import { uploadImage, uploadVideo, uploadDocument, aiWrite } from "@/lib/api";
import { HelpTip } from "./HelpTip";
import { FrameEditor } from "./FrameEditor";
import { effectiveState } from "@/lib/publish";
import { linkedEndIso } from "@/lib/linked";

const resolve = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const AI_ACTIONS = [
  { mode: "rewrite", label: "Reformuler", needsText: true },
  { mode: "improve", label: "Améliorer", needsText: true },
  { mode: "shorten", label: "Raccourcir", needsText: true },
  { mode: "expand", label: "Développer", needsText: true },
];

// Bouton/panneau « ✨ IA » : rédige ou retravaille le texte d'un champ via GPT (français).
export const AiToolbar = ({ value, onChange, context = "un contenu" }) => {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (mode) => {
    if (busy) return;
    if (mode === "generate" && !instruction.trim()) { toast.error("Décrivez le sujet à rédiger."); return; }
    setBusy(true);
    try {
      const { text } = await aiWrite({ mode, contentType: context, instruction, text: value || "" });
      if (text) { onChange(text); toast.success("Texte généré par l'IA."); }
      else toast.error("Réponse vide, réessayez.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA, réessayez.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="ai-toolbar">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="ai-toggle-btn"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase text-electric/90 hover:text-electric border border-electric/30 rounded-full px-3 py-1.5 transition-colors">
        <Sparkles size={12} /> Assistant IA
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-electric/25 bg-[#0a0a0a] p-3 space-y-2.5" data-testid="ai-panel">
          <div className="flex gap-2">
            <input value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="ai-instruction-input"
              placeholder={`Sujet / consigne pour ${context}…`}
              className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
            <button type="button" onClick={() => run("generate")} disabled={busy} data-testid="ai-generate-btn"
              className="inline-flex items-center gap-1.5 shrink-0 bg-electric text-black rounded px-3 py-2 text-xs font-semibold hover:bg-white transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Générer
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {AI_ACTIONS.map((a) => (
              <button key={a.mode} type="button" disabled={busy || (a.needsText && !(value || "").trim())} onClick={() => run(a.mode)}
                data-testid={`ai-action-${a.mode}`}
                className="text-[11px] rounded-full border border-white/15 text-secondary2 px-3 py-1.5 hover:border-electric hover:text-electric transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {a.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-secondary2/70">Rédige en français. Vérifiez toujours le texte avant de publier.</p>
        </div>
      )}
    </div>
  );
};

export const Input = ({ label, value, onChange, placeholder, testid, type = "text" }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
    <input data-testid={testid} type={type} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none [color-scheme:dark]"
      value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </label>
);

export const Area = ({ label, value, onChange, rows = 3, aiContext, testid }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
    <textarea data-testid={testid} rows={rows} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none resize-y"
      value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    {aiContext && <AiToolbar value={value} onChange={onChange} context={aiContext} />}
  </label>
);

export const ColorInput = ({ label, value, onChange, placeholder = "#FFFFFF" }) => {
  const safe = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value || "") ? value : "#FFFFFF";
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        <input type="color" value={safe} onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 rounded border border-white/10 bg-[#0e0e0e] cursor-pointer p-1" />
        <input className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none"
          value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      </div>
    </label>
  );
};

export const Toggle = ({ label, checked, onChange, hint, testid }) => (
  <label className="flex items-center gap-2 text-xs text-secondary2 cursor-pointer select-none">
    <input type="checkbox" data-testid={testid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    {hint ? <HelpTip text={hint} /> : null}
  </label>
);

export const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
    <select data-testid={testid} className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none"
      value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

// ---- MultiSelect : choix prédéfinis + recherche + "Autre" libre, étiquettes retirables ----
// value = tableau de chaînes. suggestions = liste de chaînes prédéfinies (communes).
// Les valeurs libres saisies via « Autre » sont conservées dans la sélection et peuvent
// être remontées dans le référentiel commun via onCustomAdd.
export const MultiSelect = ({ label, value = [], suggestions = [], onChange, onCustomAdd, placeholder = "Rechercher ou ajouter…", testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  // Ferme la liste au clic extérieur (UX).
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const sel = Array.isArray(value) ? value : (value ? [value] : []);
  const all = [...new Set([...suggestions, ...sel])]; // suggestions + valeurs custom déjà choisies
  const filtered = all.filter((s) => !q || s.toLowerCase().includes(q.toLowerCase()));
  const canAddCustom = q.trim() && !all.some((s) => s.toLowerCase() === q.trim().toLowerCase());

  const toggle = (v) => {
    const next = sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v];
    onChange(next);
  };
  const addCustom = () => {
    const v = q.trim();
    if (!v) return;
    onChange([...sel, v]);
    onCustomAdd && onCustomAdd(v);
    setQ("");
  };
  return (
    <div className="block" data-testid={testid} ref={rootRef}>
      <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
      {/* Étiquettes sélectionnées */}
      {sel.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {sel.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-[11px] text-white bg-electric/15 border border-electric/40 rounded-full px-2.5 py-1">
              {v}
              <button type="button" onClick={() => toggle(v)} data-testid={`${testid}-remove`} className="text-electric hover:text-white" aria-label={`Retirer ${v}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative mt-2">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
          className="w-full text-left bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white/60 text-sm focus:border-electric outline-none flex items-center justify-between">
          {sel.length ? `${sel.length} sélectionné(s)` : placeholder}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-full rounded border border-white/10 bg-[#0a0a0a] shadow-xl max-h-56 overflow-auto">
            <div className="p-2 border-b border-white/10">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`}
                placeholder="Rechercher…" className="w-full bg-[#0e0e0e] border border-white/10 rounded px-2 py-1.5 text-white text-xs focus:border-electric outline-none" />
            </div>
            <div className="py-1">
              {filtered.map((s) => (
                <button type="button" key={s} onClick={() => toggle(s)} data-testid={`${testid}-opt`}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-white/5 ${sel.includes(s) ? "text-electric" : "text-white/80"}`}>
                  <span className={`inline-block h-3.5 w-3.5 rounded border ${sel.includes(s) ? "bg-electric border-electric" : "border-white/30"}`} />
                  {s}
                </button>
              ))}
              {canAddCustom && (
                <button type="button" onClick={addCustom} data-testid={`${testid}-add-custom`}
                  className="w-full text-left px-3 py-1.5 text-xs text-electric hover:bg-white/5 flex items-center gap-2">
                  <Plus size={12} /> Ajouter « {q.trim()} »
                </button>
              )}
              {filtered.length === 0 && !canAddCustom && <div className="px-3 py-2 text-xs text-secondary2">Aucun résultat.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ImageInput = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const { url } = await uploadImage(f); onChange(url); toast.success("Image téléversée"); }
    catch { toast.error("Échec du téléversement"); }
    finally { setBusy(false); }
  };
  return (
    <label className="block">
      {label && <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>}
      <div className="mt-2 flex items-center gap-3">
        {value ? <img src={resolve(value)} alt="" className="h-14 w-20 object-cover rounded border border-white/10 bg-[#0e0e0e]" /> : <div className="h-14 w-20 rounded border border-white/10 bg-[#0e0e0e]" />}
        <input data-testid={testid} className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none"
          value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL ou téléverser →" />
        <span className="relative inline-flex items-center gap-2 px-3 py-2 rounded border border-white/10 text-xs text-white/80 hover:border-electric hover:text-electric cursor-pointer whitespace-nowrap">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Téléverser
          <input type="file" accept="image/*" onChange={onFile} className="absolute inset-0 opacity-0 cursor-pointer" />
        </span>
      </div>
    </label>
  );
};

export const VideoInput = ({ label, value, onChange }) => {
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null); // { ok, text } — retour explicite réussi/échoué
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setResult(null);
    setProgress(0);
    try {
      const res = await uploadVideo(f, setProgress);
      const mb = (res.size || f.size) / (1024 * 1024);
      onChange(res.url);
      setResult({ ok: true, text: `Enregistrée (${mb.toFixed(1)} Mo)` });
      toast.success(`Vidéo téléversée et enregistrée (${mb.toFixed(1)} Mo)`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResult({ ok: false, text: detail || "Échec du téléversement" });
      toast.error(`Échec du téléversement : ${detail || "réessayez ou vérifiez le fichier"}`);
    } finally { setProgress(null); }
  };
  const isUploaded = value && value.startsWith("/api");
  return (
    <div className="block">
      {label && <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>}
      <div className="mt-2 flex items-center gap-3">
        {isUploaded
          ? <video src={resolve(value)} className="h-14 w-24 object-cover rounded border border-white/10 bg-[#0e0e0e]" muted />
          : <div className="h-14 w-24 rounded border border-white/10 bg-[#0e0e0e] flex items-center justify-center"><Film size={16} className="text-white/30" /></div>}
        <input className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none"
          value={value ?? ""} onChange={(e) => { setResult(null); onChange(e.target.value); }} placeholder="URL YouTube/Vimeo ou téléverser un MP4 →" />
        <span className="relative inline-flex items-center gap-2 px-3 py-2 rounded border border-white/10 text-xs text-white/80 hover:border-electric hover:text-electric cursor-pointer whitespace-nowrap">
          {progress !== null ? <><Loader2 size={14} className="animate-spin" /> {progress}%</> : <><Film size={14} /> Téléverser</>}
          <input type="file" accept="video/*" onChange={onFile} disabled={progress !== null} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="video-upload-input" />
        </span>
      </div>
      {result && (
        <div data-testid="video-upload-result" className={`mt-1.5 text-[11px] inline-flex items-center gap-1.5 ${result.ok ? "text-green-400" : "text-red-400"}`}>
          {result.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {result.text}
        </div>
      )}
      {isUploaded && !result && (
        <div className="mt-1.5 text-[11px] text-white/40">Vidéo enregistrée sur le serveur — consultable dans Médias > Diagnostic vidéos.</div>
      )}
    </div>
  );
};

export const GalleryInput = ({ label = "Galerie de médias", items = [], onChange }) => {
  const [drag, setDrag] = useState(null);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; onChange(c);
  };
  const drop = (to) => {
    if (drag === null || drag === to) { setDrag(null); return; }
    const c = [...items]; const [m] = c.splice(drag, 1); c.splice(to, 0, m); onChange(c); setDrag(null);
  };
  const setItem = (i, val) => { const c = [...items]; c[i] = val; onChange(c); };
  const typeOf = (x) => (x && typeof x === "object" && x.type === "video" ? "video" : "image");
  const urlOf = (x) => (x && typeof x === "object" ? x.url || "" : x || "");
  const frameOf = (x) => (x && typeof x === "object" ? x.frame : undefined);
  const setFrame = (i, frame) => {
    const url = urlOf(items[i]);
    const has = frame && Object.keys(frame).length > 0;
    setItem(i, has ? { type: "image", url, frame } : url);
  };
  const setType = (i, t) => {
    const cur = items[i];
    if (t === "video") setItem(i, { type: "video", url: urlOf(cur), poster: (cur && cur.poster) || "" });
    else setItem(i, urlOf(cur)); // photo = plain string
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onChange([...items, ""])} data-testid="gallery-add-photo" className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><ImagePlus size={13} /> Ajouter une photo</button>
          <button type="button" onClick={() => onChange([...items, { type: "video", url: "", poster: "" }])} data-testid="gallery-add-video" className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Film size={13} /> Ajouter une vidéo</button>
        </div>
      </div>
      {items.length === 0 && <p className="text-xs text-secondary2">Aucun média. Ajoutez des photos et/ou des vidéos.</p>}
      {items.length > 1 && <p className="text-[10px] text-secondary2/70">Astuce : glissez-déposez (poignée à gauche) ou utilisez les flèches pour changer l'ordre. L'ordre définit le carrousel.</p>}
      {items.map((it, i) => {
        const t = typeOf(it);
        return (
          <div key={i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)} onDragEnd={() => setDrag(null)}
            className={`flex items-start gap-2 rounded transition-colors ${drag === i ? "opacity-40" : ""}`} data-testid={`gallery-media-row-${i}`}>
            <span className="cursor-grab active:cursor-grabbing text-white/30 hover:text-electric shrink-0 pt-3" title="Glisser pour réordonner"><GripVertical size={16} /></span>
            <div className="flex-1 space-y-2 rounded border border-white/10 bg-[#080808] p-3">
              <div className="inline-flex rounded-full border border-white/10 overflow-hidden text-[10px]" data-testid={`gallery-type-toggle-${i}`}>
                <button type="button" onClick={() => setType(i, "image")} className={`px-3 py-1 uppercase tracking-[0.15em] transition-colors ${t === "image" ? "bg-electric text-black" : "text-white/60 hover:text-white"}`}>Photo</button>
                <button type="button" onClick={() => setType(i, "video")} className={`px-3 py-1 uppercase tracking-[0.15em] transition-colors ${t === "video" ? "bg-electric text-black" : "text-white/60 hover:text-white"}`}>Vidéo</button>
              </div>
              {t === "image"
                ? <>
                    <ImageInput value={urlOf(it)} onChange={(v) => setItem(i, frameOf(it) ? { type: "image", url: v, frame: frameOf(it) } : v)} />
                    {urlOf(it) && <FrameEditor label="Cadrage de cette photo (Mode Transformer)" src={urlOf(it)} value={frameOf(it)} onChange={(f) => setFrame(i, f)} aspect="landscape" testid={`gallery-frame-${i}`} hint="Glissez, zoomez et positionnez le sujet. Chaque photo garde son propre cadrage." />}
                  </>
                : (
                  <div className="space-y-2">
                    <VideoInput label="Vidéo (MP4 importé ou lien YouTube/Vimeo)" value={it.url || ""} onChange={(v) => setItem(i, { ...it, type: "video", url: v })} />
                    <ImageInput label="Miniature / poster (optionnel)" value={it.poster || ""} onChange={(v) => setItem(i, { ...it, type: "video", poster: v })} />
                  </div>
                )}
            </div>
            <div className="flex flex-col gap-1 pt-2">
              <button type="button" onClick={() => move(i, -1)} className="text-white/40 hover:text-electric"><ArrowUp size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} className="text-white/40 hover:text-electric"><ArrowDown size={13} /></button>
              <button type="button" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---- Per-gallery slideshow settings (reusable everywhere) ----
export const GALLERY_MODE_OPTIONS = [
  { value: "slide", label: "Horizontal (gauche ↔ droite)" },
  { value: "vertical", label: "Vertical (haut ↕ bas)" },
  { value: "fade", label: "Fondu enchaîné" },
  { value: "manual", label: "Manuel (sans animation)" },
  { value: "fixed", label: "Grille fixe (toutes les photos)" },
];
const INTERVALS = [{ value: 3, label: "3 secondes" }, { value: 5, label: "5 secondes" }, { value: 7, label: "7 secondes" }, { value: 10, label: "10 secondes" }];
export const DEFAULT_GALLERY_SETTINGS = { mode: "fade", autoplay: true, intervalSec: 5, arrows: true, dots: true, swipe: true, loop: true, pauseOnHover: true, autoplayVideo: true };

export const GallerySettingsEditor = ({ value = {}, onChange, testid = "gallery-settings" }) => {
  const s = { ...DEFAULT_GALLERY_SETTINGS, ...(value || {}) };
  const set = (k, v) => onChange({ ...s, [k]: v });
  return (
    <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3" data-testid={testid}>
      <span className="text-[10px] tracking-[0.2em] uppercase text-electric">Réglages du diaporama</span>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select label="Mode d'affichage" value={s.mode} onChange={(v) => set("mode", v)} options={GALLERY_MODE_OPTIONS} />
        {s.mode !== "fixed" && s.autoplay && (
          <Select label="Durée par image" value={s.intervalSec} onChange={(v) => set("intervalSec", Number(v))} options={INTERVALS} />
        )}
      </div>
      {s.mode !== "fixed" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 pt-1">
          <Toggle label="Défilement automatique" checked={s.autoplay} onChange={(v) => set("autoplay", v)} />
          <Toggle label="Pause au survol" checked={s.pauseOnHover} onChange={(v) => set("pauseOnHover", v)} />
          <Toggle label="Flèches de navigation" checked={s.arrows} onChange={(v) => set("arrows", v)} />
          <Toggle label="Points (indicateurs)" checked={s.dots} onChange={(v) => set("dots", v)} />
          <Toggle label="Swipe au doigt (mobile)" checked={s.swipe} onChange={(v) => set("swipe", v)} />
          <Toggle label="Lecture en boucle" checked={s.loop} onChange={(v) => set("loop", v)} />
          <Toggle label="Lecture auto des vidéos (sourdine)" checked={s.autoplayVideo !== false} onChange={(v) => set("autoplayVideo", v)} />
        </div>
      )}
    </div>
  );
};

// ---- Combined field: photos manager + slideshow settings (one-stop reusable block) ----
export const GalleryField = ({ label = "Galerie de médias (photos & vidéos)", items = [], settings = {}, onItemsChange, onSettingsChange, testid }) => (
  <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-4" data-testid={testid}>
    <GalleryInput label={label} items={items} onChange={onItemsChange} />
    {items.length > 0 && <GallerySettingsEditor value={settings} onChange={onSettingsChange} />}
  </div>
);

// ---- Custom section background editor (color / gradient / image / video + dim) ----
export const BG_TYPE_OPTIONS = [
  { value: "none", label: "Aucun (par défaut)" },
  { value: "color", label: "Couleur unie" },
  { value: "gradient", label: "Dégradé" },
  { value: "image", label: "Image" },
  { value: "video", label: "Vidéo" },
];
export const BackgroundEditor = ({ value = {}, onChange, testid }) => {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const type = v.type || "none";
  return (
    <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3" data-testid={testid}>
      <Select label="Type d'arrière-plan" value={type} onChange={(val) => set("type", val)} options={BG_TYPE_OPTIONS} />
      {type === "color" && <ColorInput label="Couleur" value={v.color} onChange={(val) => set("color", val)} placeholder="#050505" />}
      {type === "gradient" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ColorInput label="Couleur de départ" value={v.from} onChange={(val) => set("from", val)} placeholder="#0b1220" />
          <ColorInput label="Couleur d'arrivée" value={v.to} onChange={(val) => set("to", val)} placeholder="#050505" />
          <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Angle ({v.angle ?? 160}°)</span>
            <input type="range" min="0" max="360" value={v.angle ?? 160} onChange={(e) => set("angle", Number(e.target.value))} className="mt-3 w-full accent-electric" /></label>
        </div>
      )}
      {type === "image" && <ImageInput label="Image de fond" value={v.image} onChange={(val) => set("image", val)} />}
      {type === "video" && <VideoInput label="Vidéo de fond (MP4)" value={v.video} onChange={(val) => set("video", val)} />}
      {["image", "video", "color", "gradient"].includes(type) && (
        <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Voile d'assombrissement ({v.dim ?? 55}%)</span>
          <input type="range" min="0" max="90" value={v.dim ?? 55} onChange={(e) => set("dim", Number(e.target.value))} className="mt-3 w-full accent-electric" />
          <span className="text-[10px] text-secondary2/70">Plus le voile est fort, plus le texte reste lisible.</span></label>
      )}
    </div>
  );
};

// ---- Generic action buttons editor (fully customizable per content item) ----
const ACTION_TYPE_OPTS = [
  { value: "form", label: "Formulaire (inscription / candidature)" },
  { value: "internal", label: "Page interne" },
  { value: "external", label: "Lien externe" },
  { value: "video", label: "Vidéo (lecteur)" },
  { value: "podcast", label: "Podcast (lien)" },
  { value: "contact", label: "Nous contacter" },
  { value: "product", label: "Commande / Produit (lien)" },
];
const SUBMISSION_TYPE_OPTS = [
  { value: "inscription", label: "Inscription" },
  { value: "formation", label: "Formation" },
  { value: "projet", label: "Projet / Tournage (candidature)" },
  { value: "festival", label: "Festival (participation)" },
  { value: "casting", label: "Casting" },
  { value: "contact", label: "Contact / Autre" },
];

export const ActionsEditor = ({ label = "Boutons & actions", items, onChange }) => {
  const list = Array.isArray(items) ? items : [];
  const add = () => onChange([...list, { id: Math.random().toString(36).slice(2), label: "", type: "form", submissionType: "inscription", style: "primary", roles: [], enabled: true }]);
  const patch = (i, up) => onChange(list.map((it, x) => (x === i ? { ...it, ...up } : it)));
  const remove = (i) => onChange(list.filter((_, x) => x !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };

  return (
    <div className="space-y-3" data-testid="actions-editor">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-1.5">{label}<HelpTip text="Boutons affichés sur la fiche. Choisissez le texte et l'action (formulaire, page interne, lien, vidéo…). Laissez vide pour n'afficher aucun bouton." /></span>
        <button type="button" onClick={add} data-testid="action-add-btn" className="inline-flex items-center gap-1.5 text-xs text-electric hover:text-white border border-electric/30 rounded-full px-3 py-1.5"><Plus size={13} /> Ajouter un bouton</button>
      </div>
      {list.length === 0 && <p className="text-xs text-secondary2/70">Aucun bouton. Ajoutez-en un, ou laissez vide (aucun bouton n'apparaîtra sur le site).</p>}
      {list.map((a, i) => (
        <div key={a.id || i} className="rounded border border-white/10 bg-[#050505] p-3 space-y-3" data-testid={`action-item-${i}`}>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-secondary2">#{i + 1}</span>
            <div className="flex-1" />
            <Toggle label="Actif" checked={a.enabled !== false} onChange={(v) => patch(i, { enabled: v })} />
            <button type="button" onClick={() => move(i, -1)} className="text-secondary2 hover:text-white p-1"><ArrowUp size={14} /></button>
            <button type="button" onClick={() => move(i, 1)} className="text-secondary2 hover:text-white p-1"><ArrowDown size={14} /></button>
            <button type="button" onClick={() => remove(i)} data-testid={`action-remove-${i}`} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Texte du bouton" value={a.label} onChange={(v) => patch(i, { label: v })} placeholder="S'inscrire, Découvrir…" />
            <Select label="Action" value={a.type} onChange={(v) => patch(i, { type: v })} options={ACTION_TYPE_OPTS} />
            <Select label="Style" value={a.style || "primary"} onChange={(v) => patch(i, { style: v })} options={[{ value: "primary", label: "Principal (plein)" }, { value: "secondary", label: "Secondaire (bordure)" }]} />
          </div>
          {a.type === "form" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select label="Type de demande" value={a.submissionType || "inscription"} onChange={(v) => patch(i, { submissionType: v })} options={SUBMISSION_TYPE_OPTS} />
              <Input label="Rôles proposés (séparés par des virgules)" value={(a.roles || []).join(", ")} onChange={(v) => patch(i, { roles: v.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Acteur, Technicien, Bénévole" />
              <Input label="Titre du formulaire (optionnel)" value={a.formHeading} onChange={(v) => patch(i, { formHeading: v })} />
              <Input label="Sous-texte du formulaire (optionnel)" value={a.formIntro} onChange={(v) => patch(i, { formIntro: v })} />
            </div>
          )}
          {a.type === "internal" && <Input label="Chemin interne (ex : /festival, /formation/mon-atelier)" value={a.href} onChange={(v) => patch(i, { href: v })} placeholder="/festival" />}
          {(a.type === "external" || a.type === "product" || a.type === "podcast" || a.type === "video") && (
            <Input label={a.type === "video" ? "URL de la vidéo (YouTube/MP4)" : "URL / lien"} value={a.url} onChange={(v) => patch(i, { url: v })} placeholder="https://…" />
          )}
        </div>
      ))}
    </div>
  );
};

export const MediaBlock = ({ item, onPatch }) => (  <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3">
    <Select label="Type de média affiché" value={item.mediaType || "image"} onChange={(v) => onPatch({ mediaType: v })}
      options={[{ value: "image", label: "Image" }, { value: "slideshow", label: "Diaporama" }, { value: "video", label: "Vidéo" }]} />
    {(item.mediaType || "image") === "image" && <ImageInput label="Image" value={item.image} onChange={(v) => onPatch({ image: v })} />}
    {item.mediaType === "slideshow" && <GalleryInput label="Images du diaporama" items={item.gallery || []} onChange={(v) => onPatch({ gallery: v })} />}
    {item.mediaType === "video" && <>
      <VideoInput label="Vidéo (MP4 uploadé ou URL YouTube/Vimeo)" value={item.videoUrl} onChange={(v) => onPatch({ videoUrl: v })} />
      <ImageInput label="Miniature (image de couverture, optionnel)" value={item.image} onChange={(v) => onPatch({ image: v })} />
    </>}
  </div>
);

// Status + optional scheduled publish date. Stores publishAt as UTC ISO string.
const STATUS_SCHED = [
  { value: "published", label: "Publié (visible)" },
  { value: "scheduled", label: "Programmé (publication auto)" },
  { value: "hidden", label: "Masqué" },
];
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
export const ScheduleControls = ({ status, publishAt, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <Select label="Visibilité" value={status || "published"} onChange={(v) => onChange({ status: v })} options={STATUS_SCHED} />
    {status === "scheduled" && (
      <label className="block">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Date & heure de publication auto</span>
        <input type="datetime-local" value={toLocalInput(publishAt)} onChange={(e) => onChange({ publishAt: e.target.value ? new Date(e.target.value).toISOString() : "" })}
          className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
      </label>
    )}
  </div>
);

// ---- Custom registration questions (per formation / per news item) ----
const QUESTION_TYPES = [
  { value: "text", label: "Texte court" },
  { value: "textarea", label: "Texte long" },
  { value: "tel", label: "Téléphone" },
  { value: "email", label: "E-mail" },
];
export const QuestionsEditor = ({ items = [], onChange, testid = "questions" }) => {
  const list = items || [];
  const patch = (i, p) => { const c = [...list]; c[i] = { ...c[i], ...p }; onChange(c); };
  const add = () => onChange([...list, { id: `q-${Date.now().toString(36)}`, label: "", type: "text", required: false }]);
  return (
    <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2">Questions du formulaire d'inscription <HelpTip text="Nom, e-mail et téléphone sont toujours demandés. Ajoutez ici vos questions supplémentaires (ex : âge, expérience, motivation). Elles apparaissent dans le formulaire et les réponses arrivent dans la Boîte de réception." /></span>
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter une question</button>
      </div>
      {list.length === 0 && <p className="text-[11px] text-secondary2">Aucune question supplémentaire. Seuls nom, e-mail et téléphone seront demandés.</p>}
      {list.map((q, i) => (
        <div key={q.id || i} className="grid grid-cols-1 md:grid-cols-[1fr_150px_auto_auto] gap-2 items-end" data-testid={`${testid}-item-${i}`}>
          <Input label={`Question ${i + 1}`} value={q.label} onChange={(v) => patch(i, { label: v })} placeholder="Ex : Quelle est votre expérience ?" />
          <Select label="Type" value={q.type || "text"} onChange={(v) => patch(i, { type: v })} options={QUESTION_TYPES} />
          <label className="inline-flex items-center gap-2 text-xs text-white/70 pb-2.5"><input type="checkbox" checked={!!q.required} onChange={(e) => patch(i, { required: e.target.checked })} /> Obligatoire</label>
          <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400 pb-2.5"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};
const PUBLISH_STATES = [
  { value: "draft", label: "Brouillon", desc: "Invisible sur le site" },
  { value: "coming", label: "Bientôt disponible", desc: "Visible · action verrouillée" },
  { value: "available", label: "Disponible", desc: "Visible · action débloquée" },
  { value: "past", label: "Passé", desc: "Visible · archivé · action fermée" },
];
const EFF_LABEL = { draft: "Brouillon (invisible)", coming: "Bientôt disponible", available: "Disponible", past: "Passé (archivé)" };
const EFF_COLOR = { draft: "text-white/50 border-white/15", coming: "text-amber-400 border-amber-400/40", available: "text-electric border-electric/40", past: "text-purple-300 border-purple-400/40" };

export const PublishControls = ({ item = {}, onChange, content = {}, testid = "publish" }) => {
  const base = item.publishStatus
    || (item.status === "published" ? "available" : (item.status === "hidden" || item.status === "archived" || item.status === "scheduled") ? "draft" : "available");
  const autoEnd = linkedEndIso(content, item);
  const eff = effectiveState(item, autoEnd);
  const hasSchedule = !!(item.comingAt || item.availableAt);
  const autoPast = !!autoEnd; // linked content has an end date → auto-past is driven by it

  const makeAvailableNow = () => {
    if (window.confirm("Rendre ce contenu DISPONIBLE immédiatement ? Toute programmation en cours sera annulée. Vos informations sont conservées.")) {
      onChange({ publishStatus: "available", comingAt: "", availableAt: "", pastAt: "" });
    }
  };
  const backToDraft = () => onChange({ publishStatus: "draft", comingAt: "", availableAt: "", pastAt: "" });
  const markPast = () => onChange({ publishStatus: "past" });
  const cancelSchedule = () => onChange({ comingAt: "", availableAt: "", pastAt: "" });

  return (
    <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 space-y-4" data-testid={`${testid}-controls`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric inline-flex items-center gap-2">Publication <HelpTip text="Brouillon = invisible. Bientôt disponible = visible mais bouton d'action verrouillé. Disponible = tout débloqué. Passé = visible mais archivé (l'action d'inscription est fermée), idéal pour un événement/une formation déjà terminé. La programmation applique ces états automatiquement aux dates choisies." /></span>
        <span className={`inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${EFF_COLOR[eff]}`} data-testid={`${testid}-effective`}>● En ligne : {EFF_LABEL[eff]}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PUBLISH_STATES.map((s) => (
          <button key={s.value} type="button" data-testid={`${testid}-state-${s.value}`}
            onClick={() => onChange({ publishStatus: s.value })}
            className={`text-left rounded-lg border p-3 transition-colors ${base === s.value ? "border-electric bg-electric/10" : "border-white/10 hover:border-white/25"}`}>
            <div className={`text-sm font-semibold ${base === s.value ? "text-electric" : "text-white"}`}>{s.label}</div>
            <div className="text-[10px] text-secondary2 mt-0.5">{s.desc}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={makeAvailableNow} data-testid={`${testid}-make-available`}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-electric text-black font-semibold hover:bg-white transition-colors"><Zap size={13} /> Rendre disponible maintenant</button>
        <button type="button" onClick={backToDraft} data-testid={`${testid}-back-draft`}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"><FileEdit size={13} /> Remettre en brouillon</button>
        <button type="button" onClick={markPast} data-testid={`${testid}-mark-past`}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-purple-400/40 text-purple-300 hover:text-white hover:border-purple-300 transition-colors"><Archive size={13} /> Marquer comme passé</button>
      </div>

      <div className="border-t border-white/10 pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><CalendarClock size={13} /> Programmation automatique</span>
          {hasSchedule && <button type="button" onClick={cancelSchedule} data-testid={`${testid}-cancel-schedule`} className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-red-400"><X size={12} /> Annuler la programmation</button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Devient « Bientôt disponible » le</span>
            <input type="datetime-local" value={toLocalInput(item.comingAt)} data-testid={`${testid}-coming-at`}
              onChange={(e) => onChange({ comingAt: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Devient « Disponible » le</span>
            <input type="datetime-local" value={toLocalInput(item.availableAt)} data-testid={`${testid}-available-at`}
              onChange={(e) => onChange({ availableAt: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
          </label>
          <label className="block sm:col-span-2 md:col-span-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Devient « Passé » le</span>
            <input type="datetime-local" value={toLocalInput(item.pastAt)} data-testid={`${testid}-past-at`} disabled={autoPast}
              onChange={(e) => onChange({ pastAt: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="mt-2 w-full bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none disabled:opacity-40 disabled:cursor-not-allowed" />
          </label>
        </div>
        {autoPast
          ? <p className="text-[11px] text-purple-300/80 inline-flex items-center gap-1.5"><CalendarClock size={12} /> Passe automatiquement en « Passé » à la fin du contenu lié (date de fin de l'événement / formation). Utilisez « Marquer comme passé » pour l'archiver plus tôt.</p>
          : <p className="text-[11px] text-secondary2">Laissez vide pour ne pas programmer. Une action manuelle (ci-dessus) prime toujours sur la programmation. « Devient passé le » archive le contenu sans le supprimer.</p>}
      </div>
    </div>
  );
};


export const ProgramEditor = ({ label = "Programme", items = [], onChange, withDay = false }) => {
  const pid = () => `pg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; onChange(c); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
        <button onClick={() => onChange([...items, { id: pid(), day: "", time: "", title: "", text: "" }])} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter une étape</button>
      </div>
      {items.length === 0 && <p className="text-xs text-secondary2">Aucune étape au programme.</p>}
      {items.map((p, i) => (
        <div key={p.id || i} className="rounded border border-white/10 bg-[#050505] p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-secondary2">#{i + 1}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => move(i, -1)} className="text-white/40 hover:text-electric"><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} className="text-white/40 hover:text-electric"><ArrowDown size={13} /></button>
              <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
          <div className={`grid grid-cols-1 ${withDay ? "md:grid-cols-3" : "md:grid-cols-2"} gap-3`}>
            {withDay && <Input label="Jour (ex: Jour 1)" value={p.day} onChange={(v) => patch(i, { day: v })} />}
            <Input label="Heure (ex: 18:00)" value={p.time} onChange={(v) => patch(i, { time: v })} />
            <Input label="Titre" value={p.title} onChange={(v) => patch(i, { title: v })} />
          </div>
          <Area label="Description" value={p.text} onChange={(v) => patch(i, { text: v })} rows={2} />
        </div>
      ))}
    </div>
  );
};


// Multiple photos manager (used for activity blocks → carousel on the site).
// Each photo keeps its OWN frame (Mode Transformer): stored as { url, frame }.
export const MultiImageField = ({ label, value = [], onChange, testid = "multi-image" }) => {
  const list = Array.isArray(value) ? value : [];
  const urlOf = (x) => (x && typeof x === "object" ? x.url || "" : x || "");
  const frameOf = (x) => (x && typeof x === "object" ? x.frame : undefined);
  const setAt = (i, v) => { const c = [...list]; c[i] = v; onChange(c); };
  const setUrl = (i, v) => setAt(i, frameOf(list[i]) ? { url: v, frame: frameOf(list[i]) } : v);
  const setFrame = (i, f) => { const url = urlOf(list[i]); const has = f && Object.keys(f).length > 0; setAt(i, has ? { url, frame: f } : url); };
  return (
    <div className="space-y-2">
      <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">{label}</span>
      {list.map((it, i) => (
        <div key={i} className="rounded border border-white/10 bg-[#080808] p-2 space-y-2" data-testid={`${testid}-row-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1"><ImageInput label={`Photo ${i + 1}`} value={urlOf(it)} onChange={(v) => setUrl(i, v)} /></div>
            <button onClick={() => onChange(list.filter((_, x) => x !== i))} className="mt-6 text-red-400/70 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
          </div>
          {urlOf(it) && <FrameEditor label={`Cadrage de la photo ${i + 1}`} src={urlOf(it)} value={frameOf(it)} onChange={(f) => setFrame(i, f)} aspect="landscape" testid={`${testid}-frame-${i}`} hint="Chaque photo garde son propre cadrage (X / Y / Zoom)." />}
        </div>
      ))}
      <button onClick={() => onChange([...list, ""])} className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter une photo</button>
    </div>
  );
};

// Éditeur générique de listes simples : éléments PLIÉS par défaut (titre toujours visible
// dans l'en-tête), un seul déplié à la fois → modification isolée, fini les longues listes.
export const ArrayEditor = ({ label, items = [], fields, onChange, template }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const setItem = (i, key, val) => { const c = [...items]; c[i] = { ...c[i], [key]: val }; onChange(c); };
  const summary = (it) => { const f = fields.find((x) => it[x.key]); return f ? String(it[f.key]).slice(0, 60) : ""; };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">{label}</span>
        <button onClick={() => { onChange([...items, { ...template }]); setOpenIdx(items.length); }} data-testid={`array-add-${label}`} className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter</button>
      </div>
      {items.map((it, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={i} className="rounded-lg border border-white/10 bg-[#0a0a0a]" data-testid={`array-item-${i}`}>
            <button type="button" onClick={() => setOpenIdx(isOpen ? null : i)} data-testid={`array-toggle-${i}`}
              className="w-full flex justify-between items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors">
              <span className="text-sm text-white/90 truncate">{summary(it) || `Élément #${i + 1}`}</span>
              <span className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={(e) => { e.stopPropagation(); onChange(items.filter((_, idx) => idx !== i)); }} className="text-red-400/70 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                {isOpen ? <ChevronUp size={15} className="text-electric" /> : <ChevronDown size={15} className="text-secondary2" />}
              </span>
            </button>
            {isOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 pb-4 pt-1 border-t border-white/10">
                {fields.map((f) => {
                  if (f.type === "area") return <div key={f.key} className="md:col-span-2"><Area label={f.label} value={it[f.key]} onChange={(v) => setItem(i, f.key, v)} /></div>;
                  if (f.type === "image") return <div key={f.key} className="md:col-span-2"><ImageInput label={f.label} value={it[f.key]} onChange={(v) => setItem(i, f.key, v)} /></div>;
                  if (f.type === "images") return <div key={f.key} className="md:col-span-2"><MultiImageField label={f.label} value={it[f.key]} onChange={(v) => setItem(i, f.key, v)} /></div>;
                  return <Input key={f.key} label={f.label} value={it[f.key]} onChange={(v) => setItem(i, f.key, v)} />;
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ---- Downloadable documents (reusable): name, description, file, order ----
export const DocumentsEditor = ({ label = "Documents / Ressources", items = [], onChange, testid }) => {
  const [busyIdx, setBusyIdx] = useState(null);
  const did = () => `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; onChange(c); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  const onFile = async (i, e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusyIdx(i);
    try {
      const r = await uploadDocument(f);
      patch(i, { url: r.url, filename: r.filename, size: r.size, name: items[i].name || r.filename.replace(/\.[^.]+$/, "") });
      toast.success("Document téléversé");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec du téléversement"); }
    finally { setBusyIdx(null); }
  };
  return (
    <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><FileText size={13} /> {label}</span>
        <button onClick={() => onChange([...items, { id: did(), name: "", description: "", url: "", filename: "" }])} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter un document</button>
      </div>
      {items.length === 0 && <p className="text-xs text-secondary2">Aucun document. Ajoutez PDF, programme, fiche…</p>}
      {items.map((d, i) => (
        <div key={d.id || i} className="rounded border border-white/10 bg-[#0a0a0a] p-3 space-y-3" data-testid={`document-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-secondary2">#{i + 1} {d.filename ? `· ${d.filename}` : ""}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => move(i, -1)} className="text-white/40 hover:text-electric"><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} className="text-white/40 hover:text-electric"><ArrowDown size={13} /></button>
              <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Nom du document" value={d.name} onChange={(v) => patch(i, { name: v })} placeholder="Ex : Programme de la formation" />
            <div className="flex items-end gap-2">
              <div className="flex-1"><Input label="Fichier (URL ou téléverser →)" value={d.url} onChange={(v) => patch(i, { url: v })} placeholder="/api/files/… ou URL" /></div>
              <span className="relative inline-flex items-center gap-2 px-3 py-2 rounded border border-white/10 text-xs text-white/80 hover:border-electric hover:text-electric cursor-pointer whitespace-nowrap mb-[1px]">
                {busyIdx === i ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Téléverser
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.odt" onChange={(e) => onFile(i, e)} disabled={busyIdx === i} className="absolute inset-0 opacity-0 cursor-pointer" />
              </span>
            </div>
          </div>
          <Input label="Description (optionnelle)" value={d.description} onChange={(v) => patch(i, { description: v })} />
        </div>
      ))}
    </div>
  );
};

// ---- Multiple programs per item (each with its own title + steps) ----
export const ProgramsEditor = ({ items = [], onChange, testid }) => {
  const gid = () => `prog-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; onChange(c); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  return (
    <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Programmes (vidéo, photo, montage…)</span>
        <button onClick={() => onChange([...items, { id: gid(), title: "Nouveau programme", steps: [] }])} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter un programme</button>
      </div>
      {items.length === 0 && <p className="text-xs text-secondary2">Aucun programme. Ajoutez-en un ou plusieurs.</p>}
      {items.map((p, i) => (
        <div key={p.id || i} className="rounded border border-white/10 bg-[#0a0a0a] p-3 space-y-3" data-testid={`program-block-${i}`}>
          <div className="flex items-center gap-2">
            <input value={p.title || ""} onChange={(e) => patch(i, { title: e.target.value })} placeholder="Titre du programme (ex : Programme vidéo)"
              className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
            <button onClick={() => move(i, -1)} className="text-white/40 hover:text-electric"><ArrowUp size={13} /></button>
            <button onClick={() => move(i, 1)} className="text-white/40 hover:text-electric"><ArrowDown size={13} /></button>
            <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
          </div>
          <ProgramEditor label="Étapes" items={p.steps || []} onChange={(v) => patch(i, { steps: v })} />
        </div>
      ))}
    </div>
  );
};

// ---- Reusable subtle legibility controls (shadow / border / brightness / halo) ----
// Helps images stand out from the dark background without changing the visual identity.
export const LegibilityControls = ({ value = {}, onChange, testid = "legibility" }) => {
  const e = value || {};
  const set = (k, v) => onChange({ ...e, [k]: v });
  return (
    <div className="rounded-lg border border-white/10 bg-[#080808] p-3 space-y-2.5" data-testid={testid}>
      <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Lisibilité sur fond sombre (subtil)</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select label="Ombre portée" value={String(e.shadow || 0)} onChange={(v) => set("shadow", Number(v))}
          options={[{ value: "0", label: "Aucune" }, { value: "1", label: "Légère" }, { value: "2", label: "Moyenne" }]} />
        <label className="flex items-center gap-2"><span className="text-[10px] uppercase text-secondary2 w-28">Luminosité ({e.brightness ?? 100}%)</span>
          <input type="range" min={80} max={120} step={1} value={e.brightness ?? 100} onChange={(ev) => set("brightness", Number(ev.target.value))} className="flex-1 accent-electric" data-testid={`${testid}-brightness`} /></label>
      </div>
      <div className="flex items-center gap-6 pt-0.5">
        <Toggle label="Bordure légère" checked={!!e.ring} onChange={(v) => set("ring", v)} testid={`${testid}-ring`} />
        <Toggle label="Halo discret" checked={!!e.halo} onChange={(v) => set("halo", v)} testid={`${testid}-halo`} />
      </div>
    </div>
  );
};
